import { mapState, mapActions } from 'vuex';
import { formMixin } from '@/components/forms/mixin';
import CodeInput from '@/components/generics/code-input/code-input.vue';

export default {
    mixins: [formMixin],
    components: {
        CodeInput,
    },
    data() {
        return {
            selectedMethod: null,
            code: '',
            codeSent: false,
            isVerifying: false,
            isSendingCode: false,
            secondsUntilResend: 0,
            secondsUntilExpiry: 0,
            resendTimer: null,
            expiryTimer: null,
            errorMessage: '',
            targetExpirationTime: null,
            targetResendTime: null,
            handleVisibilityChange: null,
            sendingStartTime: null,
            canShowSentState: false,
        };
    },
    computed: {
        ...mapState('user', [
            'twoFactorRequired',
            'twoFactorToken',
            'twoFactorMethods',
            'twoFactorEmailHint',
            'twoFactorPhoneHint',
            'twoFactorSecondsUntilExpiry',
        ]),
        hasEmail() {
            return this.twoFactorMethods.includes('email');
        },
        hasPhone() {
            return this.twoFactorMethods.includes('phone');
        },
        hasAuthenticator() {
            return this.twoFactorMethods.includes('authenticator');
        },
        canSendCode() {
            return !this.isSendingCode && this.secondsUntilResend === 0;
        },
        canVerify() {
            return this.code.length >= 6 && !this.isVerifying;
        },
        methodRequiresSendCode() {
            return this.selectedMethod === 'email' || this.selectedMethod === 'phone';
        },
        formattedExpiryTime() {
            const minutes = Math.floor(this.secondsUntilExpiry / 60);
            const seconds = this.secondsUntilExpiry % 60;
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        },
        hasCodeError() {
            return this.errorMessage !== '' && this.code === '';
        },
    },
    mounted() {
        this.secondsUntilExpiry = this.twoFactorSecondsUntilExpiry;
        this.startExpiryTimer();

        // Set up visibility change listener to sync timers when tab regains focus
        this.handleVisibilityChange = this.onVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        // Auto-select if only one method available
        if (this.twoFactorMethods.length === 1) {
            this.selectMethod(this.twoFactorMethods[0]);
        }
    },
    beforeDestroy() {
        this.clearTimers();
        if (this.handleVisibilityChange) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            this.handleVisibilityChange = null;
        }
    },
    methods: {
        ...mapActions('user', [
            'twoFactorSendCode',
            'twoFactorVerify',
            'clearTwoFactorChallenge',
        ]),
        ...mapActions('popup', ['setCurrentPopup']),
        ...mapActions('profile', ['setProfileStatus']),

        // Handle visibility change - recalculate timers when tab becomes visible
        // This fixes the browser throttling issue where setInterval slows down / stops in background tabs
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.updateTimersFromTargets();
            }
        },

        // Recalculate remaining seconds from stored target timestamps
        updateTimersFromTargets() {
            const now = Date.now();

            // Update expiry timer if active
            if (this.targetExpirationTime !== null) {
                const remaining = Math.max(0, Math.ceil((this.targetExpirationTime - now) / 1000));
                this.secondsUntilExpiry = remaining;
                if (remaining <= 0) {
                    this.clearExpiryTimer();
                    this.onSessionExpired();
                }
            }

            // Update resend timer if active
            if (this.targetResendTime !== null) {
                const remaining = Math.max(0, Math.ceil((this.targetResendTime - now) / 1000));
                this.secondsUntilResend = remaining;
                if (remaining <= 0) {
                    this.clearResendTimer();
                }
            }
        },

        selectMethod(method) {
            this.selectedMethod = method;
            this.code = '';
            this.codeSent = false;
            this.errorMessage = '';
            this.secondsUntilResend = 0;
            this.targetResendTime = null;
            this.sendingStartTime = null;
            this.canShowSentState = false;

            if (method === 'authenticator') {
                // Authenticator doesn't require sending code
                this.codeSent = true;
                this.$nextTick(() => {
                    if (this.$refs.codeInput) {
                        this.$refs.codeInput.focusFirst();
                    }
                });
            } else {
                // Auto-send code for email/phone methods
                this.sendCode();
            }
        },

        onCodeComplete(code) {
            // Auto-submit when 6 digits are entered
            if (code && code.length === 6 && !this.isVerifying) {
                this.verifyCode();
            }
            // For now, just ensure focus is maintained
        },

        async sendCode() {
            if (!this.canSendCode || !this.methodRequiresSendCode) return;

            this.isSendingCode = true;
            this.canShowSentState = false;
            this.sendingStartTime = Date.now();
            this.errorMessage = '';

            // Set up minimum display duration (500ms)
            const minimumDisplayPromise = new Promise(resolve => {
                setTimeout(() => {
                    this.canShowSentState = true;
                    resolve();
                }, 500);
            });

            try {
                const response = await this.twoFactorSendCode({ method: this.selectedMethod });
                
                // Wait for minimum display duration before showing "sent" state
                await minimumDisplayPromise;
                
                this.codeSent = true;
                const resendSeconds = response.seconds_til_resend_available || 60;
                this.secondsUntilResend = resendSeconds;
                this.startResendTimer(resendSeconds);

                // Focus the code input after it appears
                this.$nextTick(() => {
                    if (this.$refs.codeInput) {
                        this.$refs.codeInput.focusFirst();
                    }
                });
            } catch (error) {
                this.errorMessage = this.extractErrorMessage(error, 'Failed to send code. Please try again.');
                this.canShowSentState = true;
            } finally {
                this.isSendingCode = false;
            }
        },

        async verifyCode() {
            if (!this.canVerify) return;

            this.isVerifying = true;
            this.errorMessage = '';

            try {
                await this.twoFactorVerify({ method: this.selectedMethod, code: this.code });
                this.clearTimers();
                this.setProfileStatus({ event: 'login' });
                this.onLoginSuccess();
            } catch (error) {
                this.errorMessage = this.extractErrorMessage(error, 'Incorrect code. Please try again.');
                this.code = '';
            } finally {
                this.isVerifying = false;
            }
        },
        getRedirectPath() {
          if (this.$router.currentRoute.query.redirect) return this.$router.currentRoute.query.redirect;
          if (this.$router.currentRoute.name === 'home') return { name: 'portfolio' };
          return null;
        },
        onLoginSuccess() {
            this.setCurrentPopup(null);
            const redirect = this.getRedirectPath();
            if (redirect) {
              this.$router.push(redirect);
            }
        },

        onCancel() {
            this.clearTimers();
            this.clearTwoFactorChallenge();
            this.setCurrentPopup(null);
        },

        startResendTimer(durationSeconds) {
            this.clearResendTimer();
            // Store absolute target time
            this.targetResendTime = Date.now() + (durationSeconds * 1000);

            this.resendTimer = setInterval(() => {
                const now = Date.now();
                const remaining = Math.max(0, Math.ceil((this.targetResendTime - now) / 1000));
                this.secondsUntilResend = remaining;

                if (remaining <= 0) {
                    this.clearResendTimer();
                }
            }, 1000);
        },

        startExpiryTimer() {
            this.clearExpiryTimer();
            // Store absolute target time
            this.targetExpirationTime = Date.now() + (this.secondsUntilExpiry * 1000);

            this.expiryTimer = setInterval(() => {
                const now = Date.now();
                const remaining = Math.max(0, Math.ceil((this.targetExpirationTime - now) / 1000));
                this.secondsUntilExpiry = remaining;

                if (remaining <= 0) {
                    this.clearExpiryTimer();
                    this.onSessionExpired();
                }
            }, 1000);
        },

        onSessionExpired() {
            this.errorMessage = 'Session expired. Please log in again.';
            setTimeout(() => {
                this.onCancel();
            }, 2000);
        },

        clearResendTimer() {
            if (this.resendTimer) {
                clearInterval(this.resendTimer);
                this.resendTimer = null;
            }
            this.targetResendTime = null;
        },

        clearExpiryTimer() {
            if (this.expiryTimer) {
                clearInterval(this.expiryTimer);
                this.expiryTimer = null;
            }
            this.targetExpirationTime = null;
        },

        clearTimers() {
            this.clearResendTimer();
            this.clearExpiryTimer();
        },

        extractErrorMessage(error, defaultMessage) {
            // API returns errors as a direct array: response.data = [{ code, message, ... }]
            if (error.response && error.response.data) {
                const data = error.response.data;

                // Handle direct array of errors (API format)
                if (Array.isArray(data) && data.length > 0 && data[0].message) {
                    return data[0].message;
                }

                // Handle nested errors array
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    return data.errors[0].message;
                }

                // Handle direct message property
                if (data.message) {
                    return data.message;
                }

                // Handle OAuth error_description
                if (data.error_description) {
                    return data.error_description;
                }
            }
            return defaultMessage;
        },

        getMethodIcon(method) {
            switch (method) {
                case 'email': return 'icon-email';
                case 'phone': return 'icon-phone';
                case 'authenticator': return 'icon-lock';
                default: return 'icon-lock';
            }
        },

        getMethodLabel(method) {
            switch (method) {
                case 'email': return `Email ${this.twoFactorEmailHint}`;
                case 'phone': return `Phone (SMS) ${this.twoFactorPhoneHint}`;
                case 'authenticator': return 'Authenticator App';
                default: return method;
            }
        },

        getMethodLabelDescriptive(method) {
            if (method === 'authenticator') {
                return 'Open your authenticator app';
            }

            // Show sending state while code is being sent
            if (this.isSendingCode) {
                const target = method === 'email' ? this.twoFactorEmailHint : this.twoFactorPhoneHint;
                return method === 'email' 
                    ? `Sending code to ${target}`
                    : `Sending code to ${target}`;
            }

            // Show sent state after code has been sent
            if (this.codeSent) {
                const target = method === 'email' ? this.twoFactorEmailHint : this.twoFactorPhoneHint;
                return method === 'email'
                    ? `Code sent to ${target}`
                    : `Code sent to ${target}`;
            }

            return method;
        },

        getCodeInputLabel() {
            switch (this.selectedMethod) {
                case 'email': return 'Enter the 6-digit code from your email';
                case 'phone': return 'Enter the 6-digit code from your phone';
                case 'authenticator': return 'Enter the 6-digit code from your authenticator app';
                default: return 'Enter the 6-digit code';
            }
        },
    },
};



// WEBPACK FOOTER //
// ./src/components/account/account-two-factor-verify/account-two-factor-verify.js